import { Box, Button, Chip, CircularProgress, Divider, IconButton, TextField, Typography, useTheme } from "@mui/material";
import { useConstants } from "../hooks/UseConstants";
import CloseIcon from "@mui/icons-material/Close";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import Fetch from "../services/Fetch";
import { AsyncPaginate } from "react-select-async-paginate";
import { FormattedMessage } from "react-intl";

function WithdrawalRequestsFilter({
    onClickClose,
    onClickConfirm,
    filterWait,
    setFilterWait,
    from,
    setFrom,
    to,
    setTo,
    teachrrId,
    setTeacherId,
    teacherValue,
    setTeacherValue,
}) {
    const { language, host } = useConstants();
    const theme = useTheme();
    const isAr = language === "ar";

    const resetFilter = () => {
        setTeacherValue(null);
        setTeacherId("");
        setFrom("");
        setTo("");
    };

    const loadTeachers = async (search, _loadedOptions, { page }) => {
        const response = await Fetch(`${host}/admin/users?account_role=teacher&page=${page}${search ? `&search=${encodeURIComponent(search)}` : ""}`);
        const items = response?.data?.data?.data || [];
        const pagination = response?.data?.data || {};
        const optionsFromApi = items.map((item) => ({
            value: item.id,
            label: `${item.first_name} ${item.last_name}`,
        }));
        const options = page === 1
            ? [{ value: "", label: isAr ? "الكل" : "All" }, ...optionsFromApi]
            : optionsFromApi;

        return {
            options,
            hasMore: (pagination.current_page || 1) * (pagination.per_page || items.length || 1) < (pagination.total || items.length || 0),
            additional: { page: page + 1 },
        };
    };

    return (
        <Box
            sx={{
                backgroundColor: theme.palette.background.paper,
                width: { xs: "92%", sm: "80%", md: "58%" },
                maxWidth: 760,
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 24px 60px rgba(13,53,140,0.18)",
            }}
            dir={isAr ? "rtl" : "ltr"}
        >
            <Box
                sx={{
                    px: 3,
                    py: 2.5,
                    background: "linear-gradient(135deg, #0D358C 0%, #2563eb 100%)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <FilterAltOutlinedIcon sx={{ fontSize: 30 }} />
                    <Box>
                        <Typography variant="h6" fontWeight={800}>
                            <FormattedMessage id="filtering_earnings_record" />
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.88 }}>
                            {isAr ? "حدد المدرس والفترة الزمنية لتضييق سجل السحب." : "Choose a teacher and date range to narrow the withdrawal history."}
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClickClose} sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.12)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ p: 3 }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                    <Chip label={isAr ? "فلترة السجل المالي" : "History filtering"} color="primary" variant="outlined" />
                    <Chip label={isAr ? "مدرس + تاريخ" : "Teacher + date"} variant="outlined" />
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.75 }}>
                        <FormattedMessage id="teacher" />
                    </Typography>
                    <AsyncPaginate
                        value={teacherValue || null}
                        loadOptions={loadTeachers}
                        onChange={(option) => {
                            setTeacherValue(option || null);
                            setTeacherId(option?.value || "");
                        }}
                        additional={{ page: 1 }}
                        styles={{
                            control: (base) => ({
                                ...base,
                                minHeight: 40,
                                borderRadius: 8,
                                borderColor: "#cbd5e1",
                            }),
                            option: (provided) => ({
                                ...provided,
                                color: "black",
                            }),
                        }}
                        placeholder={isAr ? "اختر المدرس" : "Select teacher"}
                        isSearchable
                    />
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                    <TextField
                        fullWidth
                        type="date"
                        size="small"
                        label={<FormattedMessage id="start_date" />}
                        value={from}
                        onChange={(e) => setFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        fullWidth
                        type="date"
                        size="small"
                        label={<FormattedMessage id="end_date" />}
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, justifyContent: "flex-end", mt: 3 }}>
                    <Button onClick={resetFilter} variant="outlined" disabled={filterWait} sx={{ minWidth: 140 }}>
                        <FormattedMessage id="reset" />
                    </Button>
                    <Button
                        variant="contained"
                        disabled={filterWait}
                        sx={{ minWidth: 170, bgcolor: "#0D358C", "&:hover": { bgcolor: "#1d4ed8" } }}
                        startIcon={filterWait ? <CircularProgress size={18} color="inherit" /> : <FilterAltOutlinedIcon />}
                        onClick={() => {
                            setFilterWait(true);
                            onClickConfirm();
                        }}
                    >
                        {filterWait ? (isAr ? "جاري التطبيق..." : "Applying...") : <FormattedMessage id="filtering" />}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export default WithdrawalRequestsFilter;